import {APP_NAMES} from '../constants';

export const MAP_PIN_TYPES = {
    PRICE: 'PRICE',
    PROPERTY: 'PROPERTY',
    DESTINATION: 'DESTINATION',
    POI: 'POI',
    TOI: 'TOI',
};

const MAP_PIN_COLORS = {
    homeawayPrimary: '#0067db',
    homeawayPrimaryDarker: '#004DA5', // 21% darken
    homeawaySecondary: '#ff8a00',
    homeawaySecondaryDarker: '#D96400', // 15% darken
    homelidaysPrimary: '#f80',
    homelidaysPrimaryDarker: '#D20', // 15% darken
    homelidaysSecondary: '#353e44',
    homelidaysSecondaryDarker: '#0F181E', // 15% darken
    odhrPrimary: '#ff9900',
    odhrPrimaryDarker: '#D97300', // 15% darken
    odhrSecondary: '#353e44',
    odhrSecondaryDarker: '#0F181E', // 15% darken
    vrboPrimary: '#245abc',
    vrboPrimaryDarker: '#15326f', // 15% darken
    vrboSecondary: '#e85f24',
    vrboSecondaryDarker: '#c23900', // 15% darken
    white: '#fff',
};

const MARKER_ICONS = {
    standard: {
        /**
         * When changing the path, the anchor point need to be updated to the bottom center of the svg point.
         * https://developers.google.com/maps/documentation/javascript/symbols
         */
        anchor: {x: 12, y: 15},
        path: 'M 26.93,32.18 C 26.37,31.37 25.82,30.54 25.29,29.72 24.03,27.79 22.82,25.82 21.68,23.83 21.01,22.67 20.37,21.49 19.77,20.30 17.68,16.26 16.50,13.07 16.50,11.10 16.50,5.24 21.24,0.50 27.10,0.50 32.95,0.50 37.69,5.24 37.69,11.10 37.69,13.07 36.51,16.26 34.43,20.33 33.81,21.52 33.16,22.70 32.49,23.82 31.35,25.82 30.12,27.81 28.89,29.71 28.46,30.37 28.06,30.97 27.70,31.51 27.70,31.51 27.25,32.17 27.25,32.17 27.13,32.34 27.06,32.34 26.95,32.18 26.95,32.18 26.95,32.18 26.95,32.18 26.95,32.18 26.93,32.18 26.93,32.18 Z M 26.93,32.18 C 26.37,31.37 25.82,30.54 25.29,29.72 24.03,27.79 22.82,25.82 21.68,23.83 21.01,22.67 20.37,21.49 19.77,20.30 17.68,16.26 16.50,13.07 16.50,11.10 16.50,5.24 21.24,0.50 27.10,0.50 32.95,0.50 37.69,5.24 37.69,11.10 37.69,13.07 36.51,16.26 34.43,20.33 33.81,21.52 33.16,22.70 32.49,23.82 31.35,25.82 30.12,27.81 28.89,29.71 28.46,30.37 28.06,30.97 27.70,31.51 27.70,31.51 27.25,32.17 27.25,32.17 27.13,32.34 27.06,32.34 26.95,32.18 26.95,32.18 26.95,32.18 26.95,32.18 26.95,32.18 26.93,32.18 26.93,32.18 Z',
        fillOpacity: 1,
        strokeColor: '#FFF',
    },
    price: {
        path: 'M21.815 28.366C17.938 30.809 16 32.687 16 34a1 1 0 0 1-2 0c0-1.313-1.938-3.19-5.815-5.634C3.326 25.884 0 20.83 0 15 0 6.716 6.716 0 15 0c8.284 0 15 6.716 15 15 0 5.83-3.326 10.884-8.185 13.366z',
        anchor: {x: 15, y: 35},
        origin: {x: 0, y: 0},
        labelOrigin: {x: 15, y: 15},
        fillOpacity: 1,
        strokeColor: MAP_PIN_COLORS.homeawayPrimary,
        strokeWeight: 0.5,
    },
};

/**
 * Generates map icon SVG
 * @param siteName
 * @param isHighlighted
 * @param isSelected
 * @param appName
 * @returns {{fillColor, path: string, fillOpacity: number, anchor: {x: number, y: number}, strokeColor: string}|{fillColor: (string), path: string, fillOpacity: number, anchor: {x: number, y: number}, origin: {x: number, y: number}, strokeWeight: number, labelOrigin: {x: number, y: number}, strokeColor: string}}
 */
export const iconSymbol = (
    siteName,
    isHighlighted = false,
    isSelected = false,
    appName
) => {
    let colorKeyBrand;
    let colorKeyImportance = isHighlighted ? 'Secondary' : 'Primary';
    let colorKeyModifier = isSelected ? 'Darker' : '';

    if (appName === APP_NAMES.LANDINGAPP) {
        const fillColor = isHighlighted
            ? MAP_PIN_COLORS.homeawayPrimary
            : MAP_PIN_COLORS.white;
        return {
            ...MARKER_ICONS.price,
            fillColor,
        };
    }
    if (siteName && siteName.includes('homelidays')) {
        colorKeyBrand = 'homelidays';
    } else if (siteName && siteName.includes('vrbo')) {
        colorKeyBrand = 'vrbo';
    } else if (siteName && siteName.includes('odhr')) {
        colorKeyBrand = 'odhr';
    } else {
        colorKeyBrand = 'homeaway';
    }
    const fillColor =
        MAP_PIN_COLORS[
            `${colorKeyBrand}${colorKeyImportance}${colorKeyModifier}`
        ];
    let markerIcon = MARKER_ICONS.standard;
    if (appName === APP_NAMES.SERP) {
        markerIcon = {
            ...markerIcon,
            anchor: {x: 27, y: 32},
        };
    }
    return {
        ...markerIcon,
        fillColor,
    };
};

/**
 * Generates the icon SVG for the `mapCentralPin` which is used to mark cities and POIs related to the user search.
 */
export const mapCentralPinSymbol = () => {
    return {
        /**
         * When changning the path, the anchor point need to be updated to the bottom center of the svg point.
         * https://developers.google.com/maps/documentation/javascript/symbols
         */
        anchor: {x: 27, y: 32},
        path: 'M24.766 8.315C24.766 3.73 21.006 0 16.383 0 11.76 0 8 3.73 8 8.315c0 4.272 3.267 7.799 7.452 8.26v15.76h1.863v-15.76c4.185-.461 7.451-3.988 7.451-8.26z',
        fillColor: '#323F4D',
        fillOpacity: 1,
        strokeColor: '#323F4D',
    };
};

export const getMapToiSymbol = (fillColor) => {
    return {
        /**
         * When changing the path, the anchor point need to be updated to the bottom center of the svg point.
         * https://developers.google.com/maps/documentation/javascript/symbols
         */
        path: 'M1,10a9,9 0 1,0 18,0a9,9 0 1,0 -18,0',
        fillColor,
        fillOpacity: 1,
        strokeColor: '#FFF',
        strokeWeight: 2,
        labelOrigin: {x: 10, y: 10},
    };
};

export const getPointSymbol = (fillColor, scale = 1) => {
    return {
        /**
         * When changing the path, the anchor point need to be updated to the bottom center of the svg point.
         * https://developers.google.com/maps/documentation/javascript/symbols
         */
        path: 'M1,10a9,9 0 1,0 18,0a9,9 0 1,0 -18,0',
        fillColor,
        fillOpacity: 1,
        strokeColor: '#FFF',
        strokeWeight: 2,
        scale,
        labelOrigin: {x: 10, y: 10},
    };
};

export const LANDINGAPP_MARKER_ICON = {
    DESTINATION: {
        path: 'M21.815 28.366C17.938 30.809 16 32.687 16 34a1 1 0 0 1-2 0c0-1.313-1.938-3.19-5.815-5.634C3.326 25.884 0 20.83 0 15 0 6.716 6.716 0 15 0c8.284 0 15 6.716 15 15 0 5.83-3.326 10.884-8.185 13.366z',
        anchor: {x: 15, y: 35},
        origin: {x: 0, y: 0},
        labelOrigin: {x: 15, y: 15},
        fillColor: '#fff', // white
        fillOpacity: 1,
        strokeColor: '#0067db', // blue
        strokeWeight: 0.5,
    },
    PRICE: 'data:image/svg+xml,%3Csvg%20width%3D%221px%22%20height%3D%221px%22%20viewBox%3D%220%200%201%201%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%3Cg%20id%3D%22Symbols%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20id%3D%22marker%22%3E%3Crect%20id%3D%22Rectangle%22%20x%3D%223%22%20y%3D%2231%22%20width%3D%221%22%20height%3D%221%22%20rx%3D%220.5%22%3E%3C%2Frect%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E',
    POINT: {
        /**
         * When changing the path, the anchor point need to be updated to the bottom center of the svg point.
         * https://developers.google.com/maps/documentation/javascript/symbols
         */
        path: 'M1,10a9,9 0 1,0 18,0a9,9 0 1,0 -18,0',
        fillOpacity: 1,
        strokeColor: '#FFF',
        strokeWeight: 2,
        anchor: {x: 7, y: 1},
    },
};

export const getBoundsByMarkers = (markers, google) => {
    const newMarkers = [...markers];
    const {LatLngBounds, LatLng} = google?.maps;
    const boundsByMarkers = newMarkers
        .map(({position}) => new LatLng(position.lat, position.lng))
        .reduce(
            (newBounds, point) => newBounds.extend(point),
            new LatLngBounds()
        );

    const {lat: neLat, lng: neLong} = boundsByMarkers.getNorthEast();
    const {lat: swLat, lng: swLong} = boundsByMarkers.getSouthWest();

    return {
        neLat: neLat(),
        neLong: neLong(),
        swLat: swLat(),
        swLong: swLong(),
    };
};

export const getMarkerUuid = (marker) => {
    return `${marker.position.lat}${marker.position.lng}`;
};
